import { encodePcm16 } from './pcmEncoder';
import type {
  ElevenLabsConfig,
  ElevenLabsMessage,
  VoiceInputStatus,
} from './types';

const CONNECTION_TIMEOUT_MS = 5000; // 5 seconds
const MAX_RECORDING_MS = 300000; // 5 minutes

/**
 * WebSocket manager for ElevenLabs realtime speech-to-text.
 * Handles microphone input, audio encoding, and WebSocket communication.
 */
export class ElevenLabsWebSocket {
  private ws: WebSocket | null = null;
  private mediaStream: MediaStream | null = null;
  private audioContext: AudioContext | null = null;
  private workletNode: AudioWorkletNode | null = null;
  private scriptProcessor: ScriptProcessorNode | null = null;
  private config: ElevenLabsConfig;
  private status: VoiceInputStatus = 'idle';
  private connectionTimeout: ReturnType<typeof setTimeout> | null = null;
  private recordingTimeout: ReturnType<typeof setTimeout> | null = null;
  // Flag to prevent onclose from overwriting error status during cleanup
  private preservingStatus = false;

  constructor(config: ElevenLabsConfig) {
    this.config = config;
  }

  /**
   * Start recording audio and streaming to ElevenLabs.
   * Requests microphone access, connects WebSocket, and begins audio capture.
   */
  async startRecording(): Promise<void> {
    this.setStatus('connecting');

    try {
      // Request microphone access FIRST to stay in user gesture context
      this.mediaStream = await navigator.mediaDevices.getUserMedia({
        audio: {
          sampleRate: 16000,
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true,
        },
      });
    } catch (err) {
      if (err instanceof Error) {
        if (err.name === 'NotAllowedError') {
          this.handleError('Microphone access denied');
        } else if (err.name === 'NotFoundError') {
          this.handleError('No microphone found');
        } else {
          console.error('Microphone access failed:', err);
          this.handleError("We couldn't access your microphone. Try again.");
        }
      } else {
        this.handleError('Microphone access failed');
      }
      return;
    }

    // Connect WebSocket
    const url = `wss://api.elevenlabs.io/v1/speech-to-text/realtime?model_id=scribe_v2_realtime&token=${this.config.token}&audio_format=pcm_16000&commit_strategy=vad`;

    try {
      this.ws = new WebSocket(url);
    } catch {
      this.handleError('WebSocket connection failed');
      this.cleanup();
      return;
    }

    // Set connection timeout
    this.connectionTimeout = setTimeout(() => {
      if (this.status === 'connecting') {
        this.handleError('Connection timeout');
        this.stopRecording();
      }
    }, CONNECTION_TIMEOUT_MS);

    this.ws.onopen = () => {
      // Clear connection timeout
      if (this.connectionTimeout) {
        clearTimeout(this.connectionTimeout);
        this.connectionTimeout = null;
      }

      this.setStatus('recording');

      // Set max recording timeout (5 minutes)
      this.recordingTimeout = setTimeout(() => {
        this.stopRecording();
      }, MAX_RECORDING_MS);

      // Start audio capture
      this.startAudioCapture();
    };

    this.ws.onmessage = event => {
      try {
        const message = JSON.parse(event.data) as ElevenLabsMessage;
        this.handleMessage(message);
      } catch {
        // Failed to parse message - ignore malformed responses
      }
    };

    this.ws.onclose = () => {
      this.cleanup();
      // Only transition to idle if not preserving status (e.g., during error cleanup)
      if (!this.preservingStatus) {
        this.setStatus('idle');
      }
    };

    this.ws.onerror = () => {
      this.handleError('WebSocket error');
    };
  }

  /**
   * Stop recording and close connection.
   * @param preserveStatus - If true, don't change status (used when cleaning up after error)
   */
  stopRecording(preserveStatus = false): void {
    // Set flag so onclose handler doesn't override status
    this.preservingStatus = preserveStatus;

    // Clear timeouts
    if (this.connectionTimeout) {
      clearTimeout(this.connectionTimeout);
      this.connectionTimeout = null;
    }
    if (this.recordingTimeout) {
      clearTimeout(this.recordingTimeout);
      this.recordingTimeout = null;
    }

    // Stop all media tracks
    if (this.mediaStream) {
      this.mediaStream.getTracks().forEach(track => track.stop());
      this.mediaStream = null;
    }

    // Close audio context
    if (this.audioContext) {
      if (this.workletNode) {
        this.workletNode.disconnect();
        this.workletNode = null;
      }
      if (this.scriptProcessor) {
        this.scriptProcessor.disconnect();
        this.scriptProcessor = null;
      }
      this.audioContext.close();
      this.audioContext = null;
    }

    // Close WebSocket (including CONNECTING state to prevent leaked connections)
    if (this.ws) {
      if (
        this.ws.readyState === WebSocket.OPEN ||
        this.ws.readyState === WebSocket.CONNECTING
      ) {
        this.ws.close(1000, 'Recording stopped');
      }
      this.ws = null;
    }

    // Only transition to idle if not preserving status (e.g., during error cleanup)
    if (!preserveStatus) {
      this.setStatus('idle');
    }
  }

  /**
   * Clean up all resources.
   * @param preserveStatus - If true, don't change status (used when cleaning up after error)
   */
  destroy(preserveStatus = false): void {
    this.stopRecording(preserveStatus);
  }

  /**
   * Start capturing audio from microphone and streaming to WebSocket.
   */
  private async startAudioCapture(): Promise<void> {
    if (!this.mediaStream) {
      return;
    }

    this.audioContext = new AudioContext({ sampleRate: 16000 });
    const source = this.audioContext.createMediaStreamSource(this.mediaStream);

    // Try to use AudioWorklet (modern approach)
    try {
      // Create a simple audio worklet processor inline
      const processorCode = `
        class AudioProcessor extends AudioWorkletProcessor {
          process(inputs, outputs, parameters) {
            const input = inputs[0];
            if (input.length > 0) {
              this.port.postMessage(input[0]);
            }
            return true;
          }
        }
        registerProcessor('audio-processor', AudioProcessor);
      `;

      const blob = new Blob([processorCode], {
        type: 'application/javascript',
      });
      const url = URL.createObjectURL(blob);

      try {
        await this.audioContext.audioWorklet.addModule(url);
      } finally {
        URL.revokeObjectURL(url);
      }

      this.workletNode = new AudioWorkletNode(
        this.audioContext,
        'audio-processor'
      );

      this.workletNode.port.onmessage = event => {
        this.sendAudioData(event.data);
      };

      source.connect(this.workletNode);
      this.workletNode.connect(this.audioContext.destination);
    } catch {
      // Fallback to ScriptProcessorNode (deprecated but more compatible)
      // Check if audioContext is still available (might have been cleaned up)
      if (!this.audioContext) {
        return;
      }

      const bufferSize = 4096;
      this.scriptProcessor = this.audioContext.createScriptProcessor(
        bufferSize,
        1,
        1
      );

      this.scriptProcessor.onaudioprocess = event => {
        const inputData = event.inputBuffer.getChannelData(0);
        this.sendAudioData(inputData);
      };

      source.connect(this.scriptProcessor);
      this.scriptProcessor.connect(this.audioContext.destination);
    }
  }

  /**
   * Encode and send audio data to WebSocket.
   */
  private sendAudioData(float32Data: Float32Array): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      return;
    }

    // Convert to PCM 16-bit
    const pcmBuffer = encodePcm16(float32Data);

    // Convert to base64
    const base64 = btoa(String.fromCharCode(...new Uint8Array(pcmBuffer)));

    // Send to ElevenLabs
    this.ws.send(
      JSON.stringify({
        message_type: 'input_audio_chunk',
        audio_base_64: base64,
        commit: false,
        sample_rate: 16000,
      })
    );
  }

  /**
   * Handle incoming WebSocket messages.
   */
  private handleMessage(message: ElevenLabsMessage): void {
    switch (message.message_type) {
      case 'session_started':
        // Session started successfully
        break;

      case 'partial_transcript':
        this.config.onPartialTranscript(message.text);
        break;

      case 'final_transcript':
      case 'committed_transcript':
        this.config.onCommittedTranscript(message.text);
        break;

      case 'input_error':
        this.handleError(message.error);
        break;
    }
  }

  /**
   * Update status and notify callback.
   */
  private setStatus(status: VoiceInputStatus): void {
    this.status = status;
    if (this.config.onStatusChange) {
      this.config.onStatusChange(status);
    }
  }

  /**
   * Handle errors and notify callback.
   */
  private handleError(error: string): void {
    this.setStatus('error');
    if (this.config.onError) {
      this.config.onError(error);
    }
  }

  /**
   * Clean up all resources (media, audio, timers) without closing the WebSocket.
   * Used when the WebSocket closes externally (onclose) to release mic/audio.
   */
  private cleanup(): void {
    if (this.connectionTimeout) {
      clearTimeout(this.connectionTimeout);
      this.connectionTimeout = null;
    }
    if (this.recordingTimeout) {
      clearTimeout(this.recordingTimeout);
      this.recordingTimeout = null;
    }

    // Stop all media tracks (release microphone)
    if (this.mediaStream) {
      this.mediaStream.getTracks().forEach(track => track.stop());
      this.mediaStream = null;
    }

    // Close audio context and disconnect nodes
    if (this.audioContext) {
      if (this.workletNode) {
        this.workletNode.disconnect();
        this.workletNode = null;
      }
      if (this.scriptProcessor) {
        this.scriptProcessor.disconnect();
        this.scriptProcessor = null;
      }
      this.audioContext.close();
      this.audioContext = null;
    }

    this.ws = null;
  }
}
